import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PrayingHands from '@/components/icons/PrayingHands';
import { IS_NATIVE } from '@/lib/nativeContext';
import { badgesFor, phoneKind } from '@/lib/getTheApp';
import { EVENTS, track } from '@/lib/analytics';

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // RequireAuthRedirect sends logged-out users here with ?next= so
  // they land back where they were headed after logging in. Both buttons
  // below used to drop it, silently falling back to the (stale, until
  // consumed) localStorage copy in auth-redirect.js instead.
  const nextParam = new URLSearchParams(location.search).get('next');
  const withNext = (path) => {
    if (!nextParam) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}next=${encodeURIComponent(nextParam)}`;
  };

  // Two named doors. A brand-new device sees "Create your account"
  // first; a device that has signed in before (flag set in App.js
  // handleUserLogin, survives logout) sees Log In first. Dennis,
  // 2026-09-21: the old "Start with a Prayer Request" read like paperwork,
  // nothing told a first-timer which button was theirs, and a tester
  // pointed out the flow creates the account BEFORE asking for the prayer
  // -- so the button says that, and the line under it carries the promise.
  const knownDevice = (() => {
    try {
      return localStorage.getItem('gr8ful.knownDevice') === '1'
        || !!localStorage.getItem('mustardSeedlingsUser');
    } catch (_) {
      return false;
    }
  })();

  // The store badges (P0211): the one for this phone, both on a computer, none inside the app itself. On a computer a
  // badge opens the store's page in a new tab; on a phone the store app takes over.
  const phone = typeof navigator === 'undefined' ? 'other' : phoneKind(navigator);
  const badges = badgesFor(phone);
  const newTab = phone === 'other' ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  const filled = 'bg-[#4E8B7C] text-white hover:bg-[#3D7265] rounded-full px-10 py-7 text-xl shadow-lg hover:shadow-xl transition-all';
  const outlined = 'border-[#4E8B7C] text-[#4E8B7C] hover:bg-[#4E8B7C]/5 rounded-full px-10 py-7 text-xl';

  const newcomerDoor = (primary) => (
    <div className="flex flex-col items-center gap-2 max-w-xs text-center" data-testid="door-new">
      <p className="text-[11px] tracking-[0.08em] uppercase font-medium text-[#8C6D1C]">New here?</p>
      <Button
        data-testid="begin-btn"
        onClick={() => navigate(withNext('/onboarding'))}
        variant={primary ? 'default' : 'outline'}
        className={primary ? filled : outlined}
      >
        Create your account
      </Button>
      <p className="text-sm text-[#5C6666] leading-snug">
        Bring your first prayer &mdash; it will be prayed for.
      </p>
    </div>
  );

  const memberDoor = (primary) => (
    <div className="flex flex-col items-center gap-2 max-w-xs text-center" data-testid="door-member">
      <p className="text-[11px] tracking-[0.08em] uppercase font-medium text-[#5C6666]">Already a member?</p>
      <Button
        data-testid="login-btn"
        onClick={() => navigate(withNext('/onboarding?login=true'))}
        variant={primary ? 'default' : 'outline'}
        className={primary ? filled : outlined}
      >
        Log In
      </Button>
      {primary && <p className="text-sm text-[#5C6666] leading-snug">Welcome back.</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#FDFBF7] to-[#F5F3EF] flex-1 flex items-center">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1745513169791-80fe358bec49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtdXN0YXJkJTIwc2VlZGxpbmclMjBzcHJvdXQlMjBzb2lsJTIwaGFuZHMlMjBuYXR1cmUlMjBzZXJlbmV8ZW58MHx8fHwxNzcxMDk1MzI2fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <img
                src="/gr8ful-logo-no-hands.png"
                alt="Gr8ful Prayers"
                className="w-64 md:w-80 mx-auto"
              />
            </div>
            
            <h2 className="-mt-4 text-3xl md:text-5xl font-medium text-[#2C3333] leading-tight flex items-center justify-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <PrayingHands size={36} color="#4E8B7C" />
              Every prayer matters.
            </h2>
            
            <p className="text-lg md:text-2xl text-[#5C6666] leading-relaxed max-w-3xl mx-auto">
              Rooted in Scripture and lifted together before Jesus. Nothing is too big or too heavy to bring before God.
            </p>

            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-8 border border-[#E8E6DF]/40 max-w-2xl mx-auto">
              <p className="text-lg text-[#2C3333] leading-relaxed italic mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                "Then you will call on me, and you will go and pray to me, and I will listen to you."
              </p>
              <p className="text-sm text-[#8C9696]">— Jeremiah 29:12 WEB</p>
            </div>

            <div className="pt-6 flex flex-col sm:flex-row items-center sm:items-start justify-center gap-7 sm:gap-12">
              {knownDevice ? (
                <>
                  {memberDoor(true)}
                  {newcomerDoor(false)}
                </>
              ) : (
                <>
                  {newcomerDoor(true)}
                  {memberDoor(false)}
                </>
              )}
            </div>

            {!IS_NATIVE && (
              <div className="flex flex-col items-center gap-3" data-testid="get-the-app">
                <p className="text-[11px] tracking-[0.08em] uppercase font-medium text-[#5C6666]">Get the app</p>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  {badges.map((badge) => (
                    <a
                      key={badge.store}
                      href={badge.href('landing')}
                      {...newTab}
                      onClick={() => track(EVENTS.APP_STORE_LINK_CLICKED, { store: badge.store, place: 'landing' })}
                      data-testid={`get-the-app-${badge.store}`}
                    >
                      {/* Google's badge carries its own clear space, so it is drawn taller to show at the same height. */}
                      <img src={badge.src} alt={badge.alt} className={badge.store === 'google' ? 'h-[60px] w-auto -my-[10px]' : 'h-10 w-auto'} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 text-sm text-[#5C6666] italic leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              "Rejoice always, pray continually, give thanks in all circumstances; for this is God's will for you in Christ Jesus."
            </p>
            <p className="mt-2 text-sm text-[#8C9696]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              — 1 Thessalonians 5:16–18
            </p>

            <div className="mt-10 pt-8 border-t border-[#E8E6DF]/60 space-y-3">
              <p className="text-xs text-[#8C9696] leading-relaxed">
                Gr8ful Prayers is a platform operated by <a href="https://www.themustardseedlings.com" target="_blank" rel="noopener noreferrer" className="text-[#4E8B7C] font-medium hover:underline">Mustard Seedlings LLC</a>.
              </p>
              <p className="text-xs text-[#8C9696] leading-relaxed">
                By creating an account or continuing, you agree to our <a href="/terms" className="text-[#4E8B7C] hover:underline">Terms of Service</a> and <a href="/privacy" className="text-[#4E8B7C] hover:underline">Privacy Policy</a>.
              </p>
              <p className="text-xs text-[#8C9696] leading-relaxed">
                Need help? <a href="/contact" className="text-[#4E8B7C] hover:underline">Contact &amp; Support</a>
              </p>
              <p className="text-xs text-[#A8B0B0] pt-2">
                &copy; 2026 Mustard Seedlings LLC. All rights reserved.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
